import { useCallback, useContext, useEffect, useState } from "react";
import { UserAuthContext } from "./user_auth_context";
import { administracaoAPI, authAPI } from "../../../api";
import { useDashboardStore } from "../../../stores/dashboard/dashboard";
import { useAdministracaoStore } from "../../../stores/administracao/administracao";
import { useUserAuthStore } from "../../../stores/global/userAuth";
import { useCentroCustoStore } from "../../../stores/comercial/centrocusto";
import { LoginForm } from "../../../types/global/UserAuthTypes";
import { useFuncionariosStore } from "../../../stores/rh/funcionarios";
import { useUsuarioStore } from "../../../stores/administracao/usuario";
import { useCargoStore } from "../../../stores/rh/cargo";
import { useMotivoAfastamentoStore } from "../../../stores/rh/motivoAfastamento";
import { useMotivoDemissaoStore } from "../../../stores/rh/motivoDemissao";
import { useGrupoAcessoStore } from "../../../stores/administracao/grupoAcesso";
import { AppContext } from "../app_context/app_context";
import { useAtributoSistemaStore } from "../../../stores/administracao/atributosSistema";
import { resetAllStores } from "../../utils/zustand_state_manager";
import { usePageLoader } from "../page_content_context";
import { usePendingNotifications } from "../../hooks/use_pending_notifications";
import { useNotificationsStore } from "../../../stores/global/notifications";

import FullScreenLoading from "../../../components/global/FullScreenLoading";

/**
 * UserAuthProvider
 */

function UserAuthProvider({ children }: { children: JSX.Element }) {
  usePendingNotifications();

  const [loading, setLoading] = useState(true)

  const { pushWarnNotification } = usePageLoader();
  const { setMessageNotice, setNoticeOpen } = useContext(AppContext)
  const { token, idUsuario, empresaId, notificacoes, isForcedUserLogout, setAuthInfos, cleanUserAuthStates, setErro, setMessageErro, clearNotificacoes, clearForcedUserLogout } = useUserAuthStore()
  const { setScreensPermissions, cleanAdministracaoStates, setLoadingScreensPermisssions } = useAdministracaoStore()
  const { setSelectedModule, cleanDashboardStates } = useDashboardStore()
  const { cleanUsuarioStates } = useUsuarioStore()
  const { cleanCentroCustoState } = useCentroCustoStore()
  const { cleanCargoState } = useCargoStore()
  const { cleanFuncionarioState } = useFuncionariosStore()
  const { cleanMotivoAfastamentoState } = useMotivoAfastamentoStore()
  const { cleanMotivoDemissaoState } = useMotivoDemissaoStore()
  const { cleanGrupoAcessoState } = useGrupoAcessoStore()
  const { setAtributoSistema, cleanAtributoSistemaState } = useAtributoSistemaStore()

  const login = async (form: LoginForm) => {
    setLoading(true);
    cleanStates();
    setErro(false);
    const response = await authAPI.login(form);
    if (response.data && response.statusCode === 200) {
      setAuthInfos(response.data);
    } else {
      setErro(true);
      setMessageErro(response.error?.Message ?? "Ocorreu um erro inesperado.");
      setLoading(false);
    }
  }

  const cleanStates = useCallback(() => {
    const theme = localStorage.getItem("theme");

    cleanUserAuthStates();
    useNotificationsStore.getState().clear();
    cleanAdministracaoStates();
    cleanDashboardStates();
    cleanUsuarioStates();
    cleanCentroCustoState();
    cleanCargoState();
    cleanFuncionarioState();
    cleanMotivoAfastamentoState();
    cleanMotivoDemissaoState();
    cleanGrupoAcessoState();
    cleanAtributoSistemaState();

    localStorage.clear();
    localStorage.setItem("theme", theme ?? "");

    sessionStorage.clear();
  }, [
    cleanAdministracaoStates,
    cleanAtributoSistemaState,
    cleanCargoState,
    cleanCentroCustoState,
    cleanDashboardStates,
    cleanFuncionarioState,
    cleanGrupoAcessoState,
    cleanMotivoAfastamentoState,
    cleanMotivoDemissaoState,
    cleanUserAuthStates,
    cleanUsuarioStates
  ]);

  const logout = useCallback(async () => {
    setNoticeOpen(true);
    setMessageNotice("Você foi deslogado com sucesso.");

    await authAPI.logout();

    cleanStates();

    resetAllStores();
  }, [
    cleanStates,
    setMessageNotice,
    setNoticeOpen
  ]);

  const appInicialization = useCallback(async () => {
    setLoading(true);
    setLoadingScreensPermisssions(true);
    const telas = await administracaoAPI.getTelas();
    if (telas.data && telas.statusCode === 200) {
      if (telas.data.length > 0) {
        /*
        setScreensPermissions(telas.data);
        const firstModule = telas.data[0].idModuloSistema;
        if (firstModule) {
          await setSelectedModule(firstModule);
        }
        await setAtributoSistema();
        */
        setScreensPermissions(telas.data);
        await setSelectedModule(0);
        await setAtributoSistema();
      }
    } else {
      await logout();
    }
    setLoadingScreensPermisssions(false);
    setLoading(false);
  }, [
    logout,
    setAtributoSistema,
    setLoadingScreensPermisssions,
    setScreensPermissions,
    setSelectedModule
  ]);

  useEffect(() => {
    if (token) {
      appInicialization();
    } else {
      cleanStates();
      setLoading(false);
    }
  }, [token, appInicialization, cleanStates]);

  useEffect(() => {
    if (isForcedUserLogout) {
      window.alert("Sessão encerrada! Faça login novamente.");
      clearForcedUserLogout();
    }
  }, [isForcedUserLogout, clearForcedUserLogout]);

  useEffect(() => {
    if (!token || !notificacoes) return;
    const store = useNotificationsStore.getState();
    store.setOwner(`${empresaId}:${idUsuario}`);
    if (store.addLoginNotifications(notificacoes) > 0) {
      pushWarnNotification("Alertas pendentes", "Existem alertas que precisam da sua atenção, clique no sino para vê-los.");
    }
    clearNotificacoes();
  }, [token, idUsuario, empresaId, notificacoes, clearNotificacoes, pushWarnNotification]);

  return (
    <UserAuthContext.Provider value={{ login, logout }}>
      <FullScreenLoading isOpen={loading} />
      {children}
    </UserAuthContext.Provider>
  )
}

export default UserAuthProvider

